import React, { useMemo, useState } from "react";
import { albums, siteInfo } from "../data/mock";
import AlbumCard from "../components/AlbumCard";
import { Reveal } from "../hooks/useReveal";
import { SpotifyIcon } from "../components/Navbar";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "film", label: "Film" },
  { id: "games", label: "Games & VR" },
  { id: "singles", label: "Singles" },
];

const Music = () => {
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(() => {
    const list = filter === "all" ? albums : albums.filter((a) => a.category === filter);
    // newest first by year (string compare works for 4-digit years)
    return [...list].sort((a, b) => b.year.localeCompare(a.year));
  }, [filter]);

  const totalTracks = useMemo(
    () => albums.reduce((sum, a) => sum + a.tracks, 0),
    []
  );

  return (
    <div className="bg-[#f4f1ec] text-black">
      <section className="pt-32 md:pt-40 pb-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] text-neutral-500">
              Music
            </span>
          </Reveal>
          <Reveal delay={80}>
            <h1
              className="mt-4 text-4xl md:text-6xl lg:text-7xl tracking-tight font-medium leading-[1.05] max-w-5xl"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              The complete discography — one click away.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-neutral-700">
              <span className="text-sm">
                <strong className="text-black">{albums.length}</strong> releases
              </span>
              <span className="w-1 h-1 rounded-full bg-neutral-400" />
              <span className="text-sm">
                <strong className="text-black">{totalTracks}</strong> tracks
              </span>
              <span className="w-1 h-1 rounded-full bg-neutral-400" />
              <a
                href={siteInfo.spotifyUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm text-black hover:underline"
              >
                <SpotifyIcon className="w-4 h-4" /> Follow on Spotify
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Sticky filter bar */}
      <section className="sticky top-16 md:top-20 z-30 bg-[#f4f1ec]/85 backdrop-blur-md border-y border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex flex-wrap items-center gap-2 md:gap-3">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            const count =
              f.id === "all"
                ? albums.length
                : albums.filter((a) => a.category === f.id).length;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-5 py-2 rounded-full text-sm font-medium tracking-tight border transition-colors ${
                  active
                    ? "bg-black text-white border-black"
                    : "bg-transparent text-neutral-700 border-black/15 hover:border-black/50 hover:text-black"
                }`}
              >
                {f.label}{" "}
                <span className={active ? "text-white/60" : "text-neutral-400"}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Albums grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {filtered.map((a, i) => (
              <Reveal key={a.id} delay={i * 60}>
                <AlbumCard album={a} />
              </Reveal>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-neutral-500 py-12 text-center">
              No releases in this category yet.
            </p>
          )}
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <p className="text-neutral-500 text-sm">
            Tip: click any cover to play the full album in the floating dock.
            Keep listening while you browse.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Music;
